import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Box,
    VStack,
    HStack,
    Heading,
    Button,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    TableContainer,
    Input,
    InputGroup,
    InputLeftElement,
    Select,
    Card,
    CardBody,
    CardHeader,
    Badge,
    IconButton,
    Menu,
    MenuButton,
    MenuList,
    MenuItem,
    useDisclosure,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalFooter,
    ModalBody,
    ModalCloseButton,
    Spinner,
    Alert,
    AlertIcon,
    useColorModeValue,
    Checkbox,
    Flex,
    Text,
    Icon,
    Switch,
    Tooltip,
    useToast,
    SimpleGrid,
    Tabs,
    TabList,
    TabPanels,
    Tab,
    TabPanel,
    FormControl,
    FormLabel,
    Textarea,
    Divider,
    Avatar,
    StatLabel,
    StatNumber,
    StatHelpText,
    Stat
} from '@chakra-ui/react';
import {
    MdSearch,
    MdRefresh,
    MdDelete,
    MdMoreVert,
    MdEdit,
    MdAdd,
    MdPerson,
    MdEmail,
    MdVerifiedUser,
    MdSupervisorAccount,
    MdGroup,
    MdHistory,
    MdFileDownload,
    MdDeleteSweep,
    MdApproval,
    MdBlock,
    MdCheckCircle,
    MdCancel,
    MdWarning
} from 'react-icons/md';
import { useSearchParams } from 'react-router-dom';
import apiService from '../services/apiService';
import { formatDateTime, getRelativeTime, formatNumber } from '../utils/helpers';
import { ToastManager } from '../components/Common/ToastManager';
import * as XLSX from 'xlsx';

/**
 * User Management Page Component for Admin Dashboard
 */
function UserManagement({ userProfile, appConfig }) {
    const [searchParams] = useSearchParams();
    const [activeTab, setActiveTab] = useState(0);
    const [users, setUsers] = useState([]);
    const [auditLogs, setAuditLogs] = useState([]);
    const [availableClients, setAvailableClients] = useState([]);
    const [statistics, setStatistics] = useState(null);
    const [timeline, setTimeline] = useState([]);
    const [loading, setLoading] = useState(true);
    const [auditLoading, setAuditLoading] = useState(false);
    const [exportLoading, setExportLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [roleFilter, setRoleFilter] = useState('all');
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [currentUser, setCurrentUser] = useState(null);
    const [currentAuditUser, setCurrentAuditUser] = useState(null);
    const [sortOrder, setSortOrder] = useState('desc');
    const [lastUpdated, setLastUpdated] = useState(null);
    
    // Pagination state for users
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [totalResults, setTotalResults] = useState(0);
    
    // Pagination state for audit logs
    const [auditCurrentPage, setAuditCurrentPage] = useState(1);
    const [auditPageSize, setAuditPageSize] = useState(20);
    const [auditTotalResults, setAuditTotalResults] = useState(0);
    const [auditUserFilter, setAuditUserFilter] = useState('');
    const [auditActionFilter, setAuditActionFilter] = useState('all');
    
    // Form state for user creation/editing
    const [userForm, setUserForm] = useState({
        email: '',
        displayName: '',
        role: 'user',
        status: 'approved',
        clientGroup: 'all'
    });
    
    // Modal states
    const { isOpen: isCreateOpen, onOpen: onCreateOpen, onClose: onCreateClose } = useDisclosure();
    const { isOpen: isEditOpen, onOpen: onEditOpen, onClose: onEditClose } = useDisclosure();
    const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
    const { isOpen: isBulkOpen, onOpen: onBulkOpen, onClose: onBulkClose } = useDisclosure();
    const { isOpen: isViewAuditOpen, onOpen: onViewAuditOpen, onClose: onViewAuditClose } = useDisclosure();

    // Refs for auto-refresh
    const refreshInterval = useRef(null);
    const searchTimeout = useRef(null);

    // Theme colors
    const cardBg = useColorModeValue('white', 'gray.800');
    const borderColor = useColorModeValue('gray.200', 'gray.700');
    const textColor = useColorModeValue('gray.600', 'gray.300');
    const headingColor = useColorModeValue('gray.800', 'white');
    const inputBg = useColorModeValue('white', 'gray.700');
    const inputBorder = useColorModeValue('gray.200', 'gray.600');
    const inputFocusBorder = useColorModeValue('brand.500', 'brand.300');
    const inputText = useColorModeValue('gray.900', 'white');

    const toast = useToast();

    /**
     * Load user data with pagination and filtering
     */
    const loadUsers = useCallback(async () => {
        try {
            setLoading(true);
            
            const params = {
                page: currentPage,
                size: pageSize,
                search: searchTerm,
                status: statusFilter !== 'all' ? statusFilter : undefined,
                role: roleFilter !== 'all' ? roleFilter : undefined,
                sortBy: 'created_at_utc',
                sortOrder: sortOrder.toUpperCase()
            };

            const response = await apiService.getUsers(params);
            
            if (response.success) {
                const usersList = response.users || [];
                const pagination = response.pagination || {};
                
                setUsers(usersList);
                setTotalResults(pagination.totalElements || 0);
                setLastUpdated(new Date());
            } else {
                console.error('Failed to load users:', response.message);
                showErrorToast(response.message || 'Failed to load users');
            }
        } catch (error) {
            console.error('Error loading users:', error);
            showErrorToast('Failed to load users');
        } finally {
            setLoading(false);
        }
    }, [currentPage, pageSize, searchTerm, statusFilter, roleFilter, sortOrder]);

    /**
     * Load audit logs with pagination and filtering
     */
    const loadAuditLogs = useCallback(async () => {
        try {
            setAuditLoading(true);
            
            const params = {
                page: auditCurrentPage,
                size: auditPageSize,
                search: auditUserFilter,
                action: auditActionFilter !== 'all' ? auditActionFilter : undefined
            };

            const response = await apiService.getUserAuditLogs(params);
            
            if (response.success) {
                const logs = response.auditLogs || [];
                const pagination = response.pagination || {};
                
                setAuditLogs(logs);
                setAuditTotalResults(pagination.totalElements || 0);
            } else {
                console.error('Failed to load audit logs:', response.message);
                showErrorToast(response.message || 'Failed to load audit logs');
            }
        } catch (error) {
            console.error('Error loading audit logs:', error);
            showErrorToast('Failed to load audit logs');
        } finally {
            setAuditLoading(false);
        }
    }, [auditCurrentPage, auditPageSize, auditUserFilter, auditActionFilter]);

    /**
     * Load additional data (statistics, clients, timeline)
     */
    const loadAdditionalData = useCallback(async () => {
        try {
            const [statsResponse, clientsResponse, timelineResponse] = await Promise.all([
                apiService.getUserStatistics(),
                apiService.getAvailableClients(),
                apiService.getUserTimeline()
            ]);

            if (statsResponse.success) {
                const stats = {
                    totalUsers: statsResponse.totalUsers || 0,
                    approvedUsers: statsResponse.statusCounts?.approved || 0,
                    pendingUsers: statsResponse.statusCounts?.pending || 0,
                    rejectedUsers: statsResponse.statusCounts?.rejected || 0,
                    adminUsers: statsResponse.roleCounts?.admin || 0,
                    regularUsers: statsResponse.roleCounts?.user || 0
                };
                setStatistics(stats);
            }

            if (clientsResponse.success) {
                const clientsList = clientsResponse.clients || [];
                setAvailableClients(clientsList);
            }
            if (timelineResponse.success) {
                const timelineData = timelineResponse.timeline || [];
                setTimeline(timelineData);
            }
        } catch (error) {
            console.error('Error loading additional data:', error);
        }
    }, []);

    /**
     * Initialize component and load data
     */
    useEffect(() => {
        loadUsers();
        loadAdditionalData();
        
        if (activeTab === 1) {
            loadAuditLogs();
        }
    }, [loadUsers, loadAdditionalData, loadAuditLogs, activeTab]);

    /**
     * Reload users when filters change
     */
    useEffect(() => {
        if (activeTab === 0) {
            // Only reload if we're on users tab
            loadUsers();
        }
    }, [statusFilter, roleFilter, sortOrder, currentPage, pageSize, activeTab, loadUsers]);

    /**
     * Debounced search effect for user search term
     */
    useEffect(() => {
        if (searchTimeout.current) {
            clearTimeout(searchTimeout.current);
        }
        
        searchTimeout.current = setTimeout(() => {
            if (activeTab === 0) {
                loadUsers();
            }
        }, 500); // 500ms debounce

        return () => {
            if (searchTimeout.current) {
                clearTimeout(searchTimeout.current);
            }
        };
    }, [searchTerm, activeTab, loadUsers]);

    /**
     * Debounced search effect for audit user filter
     */
    useEffect(() => {
        if (searchTimeout.current) {
            clearTimeout(searchTimeout.current);
        }
        
        searchTimeout.current = setTimeout(() => {
            if (activeTab === 1 && !isViewAuditOpen) {
                loadAuditLogs();
            }
        }, 500); // 500ms debounce

        return () => {
            if (searchTimeout.current) {
                clearTimeout(searchTimeout.current);
            }
        };
    }, [auditUserFilter, activeTab, isViewAuditOpen, loadAuditLogs]);

    /**
     * Reload audit logs when filters change (non-search filters)
     */
    useEffect(() => {
        if (activeTab === 1 && !isViewAuditOpen) {
            // Only reload if we're on audit tab and not viewing user-specific audit
            loadAuditLogs();
        }
    }, [auditActionFilter, auditCurrentPage, auditPageSize, activeTab, isViewAuditOpen, loadAuditLogs]);

    /**
     * Cleanup effect
     */
    useEffect(() => {
        return () => {
            if (searchTimeout.current) {
                clearTimeout(searchTimeout.current);
            }
            if (refreshInterval.current) {
                clearInterval(refreshInterval.current);
            }
        };
    }, []);

    /**
     * Handle tab change
     */
    const handleTabChange = (index) => {
        setActiveTab(index);
        if (index === 1 && auditLogs.length === 0) {
            loadAuditLogs();
        }
    };

    /**
     * Handle user creation
     */
    const handleCreateUser = async () => {
        try {
            if (!userForm.email || !userForm.displayName) {
                showErrorToast('Email and display name are required');
                return;
            }

            const response = await apiService.createUser({
                email: userForm.email,
                displayName: userForm.displayName,
                role: userForm.role,
                status: userForm.status,
                clientGroup: userForm.clientGroup
            });

            if (response.success) {
                showSuccessToast('User created successfully');
                onCreateClose();
                resetUserForm();
                loadUsers();
                loadAdditionalData();
            } else {
                showErrorToast(response.message || 'Failed to create user');
            }
        } catch (error) {
            console.error('Error creating user:', error);
            showErrorToast('Failed to create user');
        }
    };

    /**
     * Handle user editing
     */
    const handleEditUser = async () => {
        try {
            if (!currentUser) return;

            const response = await apiService.updateUser(currentUser.id, {
                displayName: userForm.displayName,
                role: userForm.role,
                status: userForm.status,
                clientGroup: userForm.clientGroup
            });

            if (response.success) {
                showSuccessToast('User updated successfully');
                onEditClose();
                setCurrentUser(null);
                resetUserForm();
                loadUsers();
                loadAdditionalData();
            } else {
                showErrorToast(response.message || 'Failed to update user');
            }
        } catch (error) {
            console.error('Error updating user:', error);
            showErrorToast('Failed to update user');
        }
    };

    /**
     * Handle user deletion
     */
    const handleDeleteUser = async () => {
        try {
            if (!currentUser) return;

            const response = await apiService.deleteUser(currentUser.id);

            if (response.success) {
                showSuccessToast('User deleted successfully');
                onDeleteClose();
                setCurrentUser(null);
                loadUsers();
                loadAdditionalData();
            } else {
                showErrorToast(response.message || 'Failed to delete user');
            }
        } catch (error) {
            console.error('Error deleting user:', error);
            showErrorToast('Failed to delete user');
        }
    };

    /**
     * Handle bulk operations
     */
    const handleBulkOperation = async (operation, comments = '') => {
        try {
            if (selectedUsers.length === 0) {
                showErrorToast('Please select users to perform bulk operation');
                return;
            }

            const response = await apiService.bulkUserOperation(selectedUsers, operation, comments);

            if (response.success) {
                showSuccessToast(`Bulk ${operation} completed successfully`);
                setSelectedUsers([]);
                onBulkClose();
                loadUsers();
                loadAdditionalData();
            } else {
                showErrorToast(response.message || `Failed to perform bulk ${operation}`);
            }
        } catch (error) {
            console.error(`Error performing bulk ${operation}:`, error);
            showErrorToast(`Failed to perform bulk ${operation}`);
        }
    };

    /**
     * Handle user status update
     */
    const handleUpdateUserStatus = async (userId, status, comments = '') => {
        try {
            const response = await apiService.updateUserStatus(userId, status, comments);

            if (response.success) {
                showSuccessToast(`User status updated to ${status}`);
                loadUsers();
                loadAdditionalData();
            } else {
                showErrorToast(response.message || 'Failed to update user status');
            }
        } catch (error) {
            console.error('Error updating user status:', error);
            showErrorToast('Failed to update user status');
        }
    };

    /**
     * Export users to Excel
     */
    const handleExportUsers = async () => {
        try {
            setExportLoading(true);

            // Fetch all users for export
            const response = await apiService.getUsers({
                page: 1,
                size: 10000,
                search: searchTerm,
                status: statusFilter !== 'all' ? statusFilter : undefined,
                role: roleFilter !== 'all' ? roleFilter : undefined
            });

            if (!response.success || !response.users) {
                showErrorToast('No user data available for export');
                return;
            }

            const allUsers = response.users || [];

            // Create workbook
            const workbook = XLSX.utils.book_new();

            // Prepare user data
            const userData = allUsers.map((user, index) => ({
                'Row': index + 1,
                'User ID': user.id,
                'Email': user.email,
                'Display Name': user.displayName || user.name,
                'Role': user.role,
                'Status': user.status,
                'Client Group': user.clientGroup || 'Not Assigned',
                'Created Date': formatDateTime(user.createdAtUtc),
                'Last Login': user.lastLoginUtc ? formatDateTime(user.lastLoginUtc) : 'Never',
                'Microsoft User ID': user.microsoftUserId || '',
                'Is Active': user.status === 'approved' ? 'Yes' : 'No'
            }));

            const userWS = XLSX.utils.json_to_sheet(userData);
            userWS['!cols'] = [
                { wch: 8 }, { wch: 15 }, { wch: 30 }, { wch: 25 }, 
                { wch: 12 }, { wch: 15 }, { wch: 20 }, { wch: 22 }, 
                { wch: 22 }, { wch: 25 }, { wch: 12 }
            ];

            XLSX.utils.book_append_sheet(workbook, userWS, 'Users');

            // Add statistics sheet
            if (statistics) {
                const statsData = [
                    { 'Metric': 'Total Users', 'Value': statistics.totalUsers || 0 },
                    { 'Metric': 'Approved Users', 'Value': statistics.approvedUsers || 0 },
                    { 'Metric': 'Pending Users', 'Value': statistics.pendingUsers || 0 },
                    { 'Metric': 'Rejected Users', 'Value': statistics.rejectedUsers || 0 },
                    { 'Metric': 'Admin Users', 'Value': statistics.adminUsers || 0 },
                    { 'Metric': 'Regular Users', 'Value': statistics.regularUsers || 0 },
                    { 'Metric': 'Export Date', 'Value': formatDateTime(new Date()) }
                ];

                const statsWS = XLSX.utils.json_to_sheet(statsData);
                statsWS['!cols'] = [{ wch: 25 }, { wch: 15 }];
                XLSX.utils.book_append_sheet(workbook, statsWS, 'Statistics');
            }

            // Generate filename
            const timestamp = new Date().toISOString().slice(0, 19).replace(/[:-]/g, '');
            const filename = `user_management_export_${allUsers.length}_users_${timestamp}.xlsx`;

            // Download file
            XLSX.writeFile(workbook, filename);
            showSuccessToast(`Exported ${allUsers.length} users to ${filename}`);

        } catch (error) {
            console.error('Export error:', error);
            showErrorToast('Export failed. Please try again.');
        } finally {
            setExportLoading(false);
        }
    };

    /**
     * Reset user form
     */
    const resetUserForm = () => {
        setUserForm({
            email: '',
            displayName: '',
            role: 'user',
            status: 'approved',
            clientGroup: 'all'
        });
    };

    /**
     * Open edit modal for user
     */
    const openEditModal = (user) => {
        setCurrentUser(user);
        setUserForm({
            email: user.email,
            displayName: user.displayName || user.name,
            role: user.role,
            status: user.status,
            clientGroup: user.clientGroup || 'all'
        });
        onEditOpen();
    };

    /**
     * Open delete modal for user
     */
    const openDeleteModal = (user) => {
        setCurrentUser(user);
        onDeleteOpen();
    };

    /**
     * Extract session ID from action details
     */
    const extractSessionId = (actionDetails) => {
        if (!actionDetails) return null;
        const match = actionDetails.match(/Session:\s*([^)]+)/);
        return match ? match[1] : null;
    };

    /**
     * Load user-specific audit logs
     */
    const loadUserAuditLogs = useCallback(async (userEmail) => {
        try {
            setAuditLoading(true);
            
            const params = {
                page: 1,
                size: 50, // Load more records for user-specific view
                search: userEmail,
                action: undefined // Show all actions for the user
            };

            const response = await apiService.getUserAuditLogs(params);
            
            if (response.success) {
                const logs = response.auditLogs || [];
                // Filter to exact email match (in case search returns partial matches)
                const userLogs = logs.filter(log => log.userEmail === userEmail);
                setAuditLogs(userLogs);
            } else {
                console.error('Failed to load user audit logs:', response.message);
                setAuditLogs([]); // Clear logs if failed
            }
        } catch (error) {
            console.error('Error loading user audit logs:', error);
            setAuditLogs([]); // Clear logs if error
        } finally {
            setAuditLoading(false);
        }
    }, []);

    /**
     * Close audit modal and restore general audit logs
     */
    const closeAuditModal = () => {
        onViewAuditClose();
        setCurrentAuditUser(null);
        setAuditUserFilter('');
        // Reload general audit logs if we're on the audit tab
        if (activeTab === 1) {
            loadAuditLogs();
        }
    };

    /**
     * Open audit modal for user
     */
    const openAuditModal = (user) => {
        setCurrentAuditUser(user);
        setAuditUserFilter(user.email);
        onViewAuditOpen();
        // Load user-specific audit logs
        loadUserAuditLogs(user.email);
    };

    /**
     * Handle user selection
     */
    const handleUserSelection = (userId, isSelected) => {
        if (isSelected) {
            setSelectedUsers(prev => [...prev, userId]);
        } else {
            setSelectedUsers(prev => prev.filter(id => id !== userId));
        }
    };

    /**
     * Handle select all users
     */
    const handleSelectAll = (isSelected) => {
        if (isSelected) {
            setSelectedUsers(users.map(user => user.id));
        } else {
            setSelectedUsers([]);
        }
    };

    /**
     * Get status color
     */
    const getStatusColor = (status) => {
        switch (status?.toLowerCase()) {
            case 'approved':
                return 'green';
            case 'pending':
                return 'yellow';
            case 'rejected':
                return 'red';
            case 'suspended':
                return 'orange';
            default:
                return 'gray';
        }
    };

    /**
     * Get role color
     */
    const getRoleColor = (role) => {
        switch (role?.toLowerCase()) {
            case 'admin':
                return 'purple';
            case 'user':
                return 'blue';
            default:
                return 'gray';
        }
    };

    /**
     * Show success toast
     */
    const showSuccessToast = (message) => {
        toast({
            title: message,
            status: 'success',
            duration: 3000,
            isClosable: true,
            position: 'top-right'
        });
    };

    /**
     * Show error toast
     */
    const showErrorToast = (message) => {
        toast({
            title: message,
            status: 'error',
            duration: 5000,
            isClosable: true,
            position: 'top-right'
        });
    };

    /**
     * Calculate total pages
     */
    const totalPages = Math.ceil(totalResults / pageSize);
    const auditTotalPages = Math.ceil(auditTotalResults / auditPageSize);

    return (
        <Box>
            <VStack spacing={6} align="stretch">
                {/* Page Header */}
                <Flex justify="space-between" align="center">
                    <Box>
                        <Heading size="lg" color={headingColor}>
                            User Management
                        </Heading>
                        <Text color={textColor} mt={1}>
                            Manage user accounts, roles, and access control
                        </Text>
                        {lastUpdated && (
                            <Text fontSize="sm" color={textColor} mt={1}>
                                Last updated: {formatDateTime(lastUpdated)}
                            </Text>
                        )}
                    </Box>
                    <HStack spacing={3}>
                        <Button
                            leftIcon={<MdRefresh />}
                            onClick={() => {
                                loadUsers();
                                loadAdditionalData();
                                if (activeTab === 1) loadAuditLogs();
                            }}
                            variant="outline"
                        >
                            Refresh
                        </Button>
                    </HStack>
                </Flex>

                {/* Statistics Cards */}
                {statistics && (
                    <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6}>
                        <Card bg={cardBg} borderColor={borderColor}>
                            <CardBody>
                                <Stat>
                                    <StatLabel color={textColor}>Total Users</StatLabel>
                                    <StatNumber color={headingColor}>{formatNumber(statistics.totalUsers || 0)}</StatNumber>
                                    <StatHelpText color={textColor}>
                                        <Icon as={MdPerson} mr={1} />
                                        All registered users
                                    </StatHelpText>
                                </Stat>
                            </CardBody>
                        </Card>

                        <Card bg={cardBg} borderColor={borderColor}>
                            <CardBody>
                                <Stat>
                                    <StatLabel color={textColor}>Approved Users</StatLabel>
                                    <StatNumber color="green.500">{formatNumber(statistics.approvedUsers || 0)}</StatNumber>
                                    <StatHelpText color={textColor}>
                                        <Icon as={MdCheckCircle} mr={1} />
                                        Active and approved
                                    </StatHelpText>
                                </Stat>
                            </CardBody>
                        </Card>

                        <Card bg={cardBg} borderColor={borderColor}>
                            <CardBody>
                                <Stat>
                                    <StatLabel color={textColor}>Pending Approval</StatLabel>
                                    <StatNumber color="yellow.500">{formatNumber(statistics.pendingUsers || 0)}</StatNumber>
                                    <StatHelpText color={textColor}>
                                        <Icon as={MdWarning} mr={1} />
                                        Awaiting approval
                                    </StatHelpText>
                                </Stat>
                            </CardBody>
                        </Card>

                        <Card bg={cardBg} borderColor={borderColor}>
                            <CardBody>
                                <Stat>
                                    <StatLabel color={textColor}>Admin Users</StatLabel>
                                    <StatNumber color="purple.500">{formatNumber(statistics.adminUsers || 0)}</StatNumber>
                                    <StatHelpText color={textColor}>
                                        <Icon as={MdSupervisorAccount} mr={1} />
                                        Administrative access
                                    </StatHelpText>
                                </Stat>
                            </CardBody>
                        </Card>
                    </SimpleGrid>
                )}

                {/* Tabbed Interface */}
                <Tabs index={activeTab} onChange={handleTabChange} colorScheme="brand">
                    <TabList>
                        <Tab>User Management</Tab>
                        <Tab>Audit Logs</Tab>
                    </TabList>

                    <TabPanels>
                        {/* User Management Tab */}
                        <TabPanel p={0} pt={6}>
                            <VStack spacing={6} align="stretch">
                                {/* Filters and Search */}
                                <Card bg={cardBg} borderColor={borderColor}>
                                    <CardHeader>
                                        <Heading size="md">Filter Users</Heading>
                                    </CardHeader>
                                    <CardBody>
                                        <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={4}>
                                            <FormControl>
                                                <FormLabel fontSize="sm">Search</FormLabel>
                                                <InputGroup>
                                                    <InputLeftElement>
                                                        <MdSearch color={textColor} />
                                                    </InputLeftElement>
                                                    <Input
                                                        placeholder="Search by email or name..."
                                                        value={searchTerm}
                                                        onChange={(e) => setSearchTerm(e.target.value)}
                                                        bg={inputBg}
                                                        borderColor={inputBorder}
                                                        color={inputText}
                                                    />
                                                </InputGroup>
                                            </FormControl>

                                            <FormControl>
                                                <FormLabel fontSize="sm">Status</FormLabel>
                                                <Select
                                                    value={statusFilter}
                                                    onChange={(e) => setStatusFilter(e.target.value)}
                                                    bg={inputBg}
                                                    borderColor={inputBorder}
                                                    color={inputText}
                                                >
                                                    <option value="all">All Status</option>
                                                    <option value="approved">Approved</option>
                                                    <option value="pending">Pending</option>
                                                    <option value="rejected">Rejected</option>
                                                    <option value="suspended">Suspended</option>
                                                </Select>
                                            </FormControl>

                                            <FormControl>
                                                <FormLabel fontSize="sm">Role</FormLabel>
                                                <Select
                                                    value={roleFilter}
                                                    onChange={(e) => setRoleFilter(e.target.value)}
                                                    bg={inputBg}
                                                    borderColor={inputBorder}
                                                    color={inputText}
                                                >
                                                    <option value="all">All Roles</option>
                                                    <option value="admin">Admin</option>
                                                    <option value="user">Support Desk User</option>
                                                </Select>
                                            </FormControl>

                                            <FormControl>
                                                <FormLabel fontSize="sm">Sort Order</FormLabel>
                                                <Select
                                                    value={sortOrder}
                                                    onChange={(e) => setSortOrder(e.target.value)}
                                                    bg={inputBg}
                                                    borderColor={inputBorder}
                                                    color={inputText}
                                                >
                                                    <option value="desc">Newest First</option>
                                                    <option value="asc">Oldest First</option>
                                                </Select>
                                            </FormControl>
                                        </SimpleGrid>
                                    </CardBody>
                                </Card>

                                {/* User Table */}
                                <Card bg={cardBg} borderColor={borderColor}>
                                    <CardHeader>
                                        <HStack justify="space-between">
                                            <Heading size="md">
                                                Users ({formatNumber(totalResults)})
                                            </Heading>
                                            <HStack spacing={3}>
                                                {selectedUsers.length > 0 && (
                                                    <Button
                                                        leftIcon={<MdEdit />}
                                                        onClick={onBulkOpen}
                                                        colorScheme="blue"
                                                        variant="outline"
                                                        size="sm"
                                                    >
                                                        Bulk Actions ({selectedUsers.length})
                                                    </Button>
                                                )}
                                                <Button
                                                    leftIcon={<MdFileDownload />}
                                                    onClick={handleExportUsers}
                                                    variant="outline"
                                                    size="sm"
                                                    isLoading={exportLoading}
                                                    loadingText="Exporting..."
                                                >
                                                    Export
                                                </Button>
                                            </HStack>
                                        </HStack>
                                    </CardHeader>
                                    <CardBody p={0}>
                                        {loading ? (
                                            <Flex justify="center" p={8}>
                                                <Spinner size="lg" color="brand.500" />
                                            </Flex>
                                        ) : users.length === 0 ? (
                                            <Flex justify="center" p={8}>
                                                <Text color={textColor}>No users found</Text>
                                            </Flex>
                                        ) : (
                                            <>
                                                <TableContainer>
                                                    <Table variant="simple" size="sm">
                                                        <Thead>
                                                            <Tr>
                                                                <Th w="50px">
                                                                    <Checkbox
                                                                        isChecked={selectedUsers.length === users.length && users.length > 0}
                                                                        isIndeterminate={selectedUsers.length > 0 && selectedUsers.length < users.length}
                                                                        onChange={(e) => handleSelectAll(e.target.checked)}
                                                                    />
                                                                </Th>
                                                                <Th>User</Th>
                                                                <Th>Role</Th>
                                                                <Th>Status</Th>
                                                                <Th>Client Group</Th>
                                                                <Th>Created</Th>
                                                                <Th>Last Login</Th>
                                                                <Th w="120px">Actions</Th>
                                                            </Tr>
                                                        </Thead>
                                                        <Tbody>
                                                            {users.map((user) => (
                                                                <Tr key={user.id}>
                                                                    <Td>
                                                                        <Checkbox
                                                                            isChecked={selectedUsers.includes(user.id)}
                                                                            onChange={(e) => handleUserSelection(user.id, e.target.checked)}
                                                                        />
                                                                    </Td>
                                                                    <Td>
                                                                        <HStack>
                                                                            <Avatar size="sm" name={user.displayName || user.name} />
                                                                            <VStack align="start" spacing={0}>
                                                                                <Text fontWeight="medium" fontSize="sm">
                                                                                    {user.displayName || user.name}
                                                                                </Text>
                                                                                <Text fontSize="xs" color={textColor}>
                                                                                    {user.email}
                                                                                </Text>
                                                                            </VStack>
                                                                        </HStack>
                                                                    </Td>
                                                                    <Td>
                                                                        <Badge
                                                                            colorScheme={getRoleColor(user.role)}
                                                                            variant="subtle"
                                                                        >
                                                                            {user.role === 'admin' ? 'Admin' : 'Support Desk User'}
                                                                        </Badge>
                                                                    </Td>
                                                                    <Td>
                                                                        <Badge
                                                                            colorScheme={getStatusColor(user.status)}
                                                                            variant="subtle"
                                                                        >
                                                                            {user.status}
                                                                        </Badge>
                                                                    </Td>
                                                                    <Td>
                                                                        <Text fontSize="sm">
                                                                            {user.clientGroup === 'all' ? 'All Clients' : user.clientGroup || 'Not Assigned'}
                                                                        </Text>
                                                                    </Td>
                                                                    <Td>
                                                                        <Text fontSize="sm" color={textColor}>
                                                                            {formatDateTime(user.createdAtUtc)}
                                                                        </Text>
                                                                    </Td>
                                                                    <Td>
                                                                        <Text fontSize="sm" color={textColor}>
                                                                            {user.lastLoginUtc ? getRelativeTime(user.lastLoginUtc) : 'Never'}
                                                                        </Text>
                                                                    </Td>
                                                                    <Td>
                                                                        <Menu>
                                                                            <MenuButton
                                                                                as={IconButton}
                                                                                icon={<MdMoreVert />}
                                                                                variant="ghost"
                                                                                size="sm"
                                                                            />
                                                                            <MenuList>
                                                                                <MenuItem
                                                                                    icon={<MdEdit />}
                                                                                    onClick={() => openEditModal(user)}
                                                                                >
                                                                                    Edit User
                                                                                </MenuItem>
                                                                                <MenuItem
                                                                                    icon={<MdHistory />}
                                                                                    onClick={() => openAuditModal(user)}
                                                                                >
                                                                                    View Audit
                                                                                </MenuItem>
                                                                                {user.status === 'pending' && (
                                                                                    <MenuItem
                                                                                        icon={<MdCheckCircle />}
                                                                                        onClick={() => handleUpdateUserStatus(user.id, 'approved')}
                                                                                        color="green.500"
                                                                                    >
                                                                                        Approve User
                                                                                    </MenuItem>
                                                                                )}
                                                                                {user.status === 'approved' && (
                                                                                    <MenuItem
                                                                                        icon={<MdBlock />}
                                                                                        onClick={() => handleUpdateUserStatus(user.id, 'suspended')}
                                                                                        color="orange.500"
                                                                                    >
                                                                                        Suspend User
                                                                                    </MenuItem>
                                                                                )}
                                                                                <MenuItem
                                                                                    icon={<MdDelete />}
                                                                                    onClick={() => openDeleteModal(user)}
                                                                                    color="red.500"
                                                                                >
                                                                                    Delete User
                                                                                </MenuItem>
                                                                            </MenuList>
                                                                        </Menu>
                                                                    </Td>
                                                                </Tr>
                                                            ))}
                                                        </Tbody>
                                                    </Table>
                                                </TableContainer>

                                                {/* Pagination */}
                                                {totalPages > 1 && (
                                                    <Flex justify="space-between" align="center" p={4} borderTop="1px" borderTopColor={borderColor}>
                                                        <Text fontSize="sm" color={textColor}>
                                                            Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalResults)} of {formatNumber(totalResults)} users
                                                        </Text>
                                                        
                                                        <HStack spacing={2}>
                                                            <Button
                                                                size="sm"
                                                                onClick={() => setCurrentPage(1)}
                                                                isDisabled={currentPage === 1}
                                                                variant="outline"
                                                            >
                                                                First
                                                            </Button>
                                                            <Button
                                                                size="sm"
                                                                onClick={() => setCurrentPage(currentPage - 1)}
                                                                isDisabled={currentPage === 1}
                                                                variant="outline"
                                                            >
                                                                Previous
                                                            </Button>
                                                            <Button
                                                                size="sm"
                                                                onClick={() => setCurrentPage(currentPage + 1)}
                                                                isDisabled={currentPage === totalPages}
                                                                variant="outline"
                                                            >
                                                                Next
                                                            </Button>
                                                            <Button
                                                                size="sm"
                                                                onClick={() => setCurrentPage(totalPages)}
                                                                isDisabled={currentPage === totalPages}
                                                                variant="outline"
                                                            >
                                                                Last
                                                            </Button>
                                                        </HStack>
                                                    </Flex>
                                                )}
                                            </>
                                        )}
                                    </CardBody>
                                </Card>
                            </VStack>
                        </TabPanel>

                        {/* Audit Logs Tab */}
                        <TabPanel p={0} pt={6}>
                            <VStack spacing={6} align="stretch">
                                {/* Audit Filters */}
                                <Card bg={cardBg} borderColor={borderColor}>
                                    <CardHeader>
                                        <Heading size="md">Filter Audit Logs</Heading>
                                    </CardHeader>
                                    <CardBody>
                                        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                                            <FormControl>
                                                <FormLabel fontSize="sm">User Email</FormLabel>
                                                <Input
                                                    placeholder="Filter by user email..."
                                                    value={auditUserFilter}
                                                    onChange={(e) => setAuditUserFilter(e.target.value)}
                                                    bg={inputBg}
                                                    borderColor={inputBorder}
                                                    color={inputText}
                                                />
                                            </FormControl>

                                            <FormControl>
                                                <FormLabel fontSize="sm">Action</FormLabel>
                                                <Select
                                                    value={auditActionFilter}
                                                    onChange={(e) => setAuditActionFilter(e.target.value)}
                                                    bg={inputBg}
                                                    borderColor={inputBorder}
                                                    color={inputText}
                                                >
                                                    <option value="all">All Actions</option>
                                                    <option value="LOGIN">Login</option>
                                                    <option value="LOGOUT">Logout</option>
                                                    <option value="CREATE">Create</option>
                                                    <option value="UPDATE">Update</option>
                                                    <option value="DELETE">Delete</option>
                                                </Select>
                                            </FormControl>

                                            <FormControl>
                                                <FormLabel fontSize="sm">Results per page</FormLabel>
                                                <Select
                                                    value={auditPageSize}
                                                    onChange={(e) => setAuditPageSize(Number(e.target.value))}
                                                    bg={inputBg}
                                                    borderColor={inputBorder}
                                                    color={inputText}
                                                >
                                                    <option value={20}>20 per page</option>
                                                    <option value={50}>50 per page</option>
                                                    <option value={100}>100 per page</option>
                                                </Select>
                                            </FormControl>
                                        </SimpleGrid>
                                    </CardBody>
                                </Card>

                                {/* Audit Logs Table */}
                                <Card bg={cardBg} borderColor={borderColor}>
                                    <CardHeader>
                                        <Heading size="md">
                                            Audit Logs ({formatNumber(auditTotalResults)})
                                        </Heading>
                                    </CardHeader>
                                    <CardBody p={0}>
                                        {auditLoading ? (
                                            <Flex justify="center" p={8}>
                                                <Spinner size="lg" color="brand.500" />
                                            </Flex>
                                        ) : auditLogs.length === 0 ? (
                                            <Flex justify="center" p={8}>
                                                <Text color={textColor}>No audit logs found</Text>
                                            </Flex>
                                        ) : (
                                            <>
                                                <TableContainer>
                                                    <Table variant="simple" size="sm">
                                                        <Thead>
                                                            <Tr>
                                                                <Th>Timestamp</Th>
                                                                <Th>User</Th>
                                                                <Th>Action</Th>
                                                                <Th>IP Address</Th>
                                                                <Th>Details</Th>
                                                            </Tr>
                                                        </Thead>
                                                        <Tbody>
                                                            {auditLogs.map((log, index) => (
                                                                <Tr key={log.id || index}>
                                                                    <Td>
                                                                        <Text fontSize="sm">
                                                                            {formatDateTime(log.createdAtUtc || log.timestamp)}
                                                                        </Text>
                                                                    </Td>
                                                                    <Td>
                                                                        <VStack align="start" spacing={0}>
                                                                            <Text fontSize="sm" fontWeight="medium">
                                                                                {log.userDisplayName || log.userEmail}
                                                                            </Text>
                                                                            <Text fontSize="xs" color={textColor}>
                                                                                {log.userEmail}
                                                                            </Text>
                                                                        </VStack>
                                                                    </Td>
                                                                    <Td>
                                                                        <Badge
                                                                            colorScheme={log.action === 'LOGIN' ? 'green' : log.action === 'LOGOUT' ? 'blue' : 'gray'}
                                                                            variant="subtle"
                                                                        >
                                                                            {log.action}
                                                                        </Badge>
                                                                    </Td>
                                                                    <Td>
                                                                        <Text fontSize="sm" fontFamily="mono">
                                                                            {log.ipAddress}
                                                                        </Text>
                                                                    </Td>
                                                                    <Td>
                                                                        <Text fontSize="sm" color={textColor} noOfLines={2}>
                                                                            {log.actionDetails || log.userAgent || 'No additional details'}
                                                                        </Text>
                                                                    </Td>
                                                                </Tr>
                                                            ))}
                                                        </Tbody>
                                                    </Table>
                                                </TableContainer>

                                                {/* Audit Pagination */}
                                                {auditTotalPages > 1 && (
                                                    <Flex justify="space-between" align="center" p={4} borderTop="1px" borderTopColor={borderColor}>
                                                        <Text fontSize="sm" color={textColor}>
                                                            Showing {((auditCurrentPage - 1) * auditPageSize) + 1} to {Math.min(auditCurrentPage * auditPageSize, auditTotalResults)} of {formatNumber(auditTotalResults)} logs
                                                        </Text>
                                                        
                                                        <HStack spacing={2}>
                                                            <Button
                                                                size="sm"
                                                                onClick={() => setAuditCurrentPage(1)}
                                                                isDisabled={auditCurrentPage === 1}
                                                                variant="outline"
                                                            >
                                                                First
                                                            </Button>
                                                            <Button
                                                                size="sm"
                                                                onClick={() => setAuditCurrentPage(auditCurrentPage - 1)}
                                                                isDisabled={auditCurrentPage === 1}
                                                                variant="outline"
                                                            >
                                                                Previous
                                                            </Button>
                                                            <Button
                                                                size="sm"
                                                                onClick={() => setAuditCurrentPage(auditCurrentPage + 1)}
                                                                isDisabled={auditCurrentPage === auditTotalPages}
                                                                variant="outline"
                                                            >
                                                                Next
                                                            </Button>
                                                            <Button
                                                                size="sm"
                                                                onClick={() => setAuditCurrentPage(auditTotalPages)}
                                                                isDisabled={auditCurrentPage === auditTotalPages}
                                                                variant="outline"
                                                            >
                                                                Last
                                                            </Button>
                                                        </HStack>
                                                    </Flex>
                                                )}
                                            </>
                                        )}
                                    </CardBody>
                                </Card>
                            </VStack>
                        </TabPanel>
                    </TabPanels>
                </Tabs>

                {/* Create User Modal */}
                <Modal isOpen={isCreateOpen} onClose={onCreateClose} size="lg">
                    <ModalOverlay />
                    <ModalContent>
                        <ModalHeader>Create New User</ModalHeader>
                        <ModalCloseButton />
                        <ModalBody>
                            <VStack spacing={4} align="stretch">
                                <FormControl isRequired>
                                    <FormLabel>Email</FormLabel>
                                    <Input
                                        type="email"
                                        placeholder="user@company.com"
                                        value={userForm.email}
                                        onChange={(e) => setUserForm({...userForm, email: e.target.value})}
                                    />
                                </FormControl>

                                <FormControl isRequired>
                                    <FormLabel>Display Name</FormLabel>
                                    <Input
                                        placeholder="John Doe"
                                        value={userForm.displayName}
                                        onChange={(e) => setUserForm({...userForm, displayName: e.target.value})}
                                    />
                                </FormControl>

                                <FormControl>
                                    <FormLabel>Role</FormLabel>
                                    <Select
                                        value={userForm.role}
                                        onChange={(e) => setUserForm({...userForm, role: e.target.value})}
                                    >
                                        <option value="user">Support Desk User</option>
                                        <option value="admin">Admin</option>
                                    </Select>
                                </FormControl>

                                <FormControl>
                                    <FormLabel>Status</FormLabel>
                                    <Select
                                        value={userForm.status}
                                        onChange={(e) => setUserForm({...userForm, status: e.target.value})}
                                    >
                                        <option value="approved">Approved</option>
                                        <option value="pending">Pending</option>
                                        <option value="suspended">Suspended</option>
                                    </Select>
                                </FormControl>

                                <FormControl>
                                    <FormLabel>Client Group</FormLabel>
                                    <Select
                                        value={userForm.clientGroup}
                                        onChange={(e) => setUserForm({...userForm, clientGroup: e.target.value})}
                                    >
                                        <option value="all">All Clients</option>
                                        {availableClients.map((client) => (
                                            <option key={client.value || client.id} value={client.value || client.id}>
                                                {client.label || client.name}
                                            </option>
                                        ))}
                                    </Select>
                                </FormControl>
                            </VStack>
                        </ModalBody>
                        <ModalFooter>
                            <Button variant="ghost" mr={3} onClick={onCreateClose}>
                                Cancel
                            </Button>
                            <Button colorScheme="brand" onClick={handleCreateUser}>
                                Create User
                            </Button>
                        </ModalFooter>
                    </ModalContent>
                </Modal>

                {/* Edit User Modal */}
                <Modal isOpen={isEditOpen} onClose={onEditClose} size="lg">
                    <ModalOverlay />
                    <ModalContent>
                        <ModalHeader>Edit User</ModalHeader>
                        <ModalCloseButton />
                        <ModalBody>
                            <VStack spacing={4} align="stretch">
                                <FormControl>
                                    <FormLabel>Email</FormLabel>
                                    <Input
                                        value={userForm.email}
                                        isReadOnly
                                        bg="gray.50"
                                    />
                                    <Text fontSize="xs" color={textColor}>
                                        Email cannot be changed
                                    </Text>
                                </FormControl>

                                <FormControl isRequired>
                                    <FormLabel>Display Name</FormLabel>
                                    <Input
                                        placeholder="John Doe"
                                        value={userForm.displayName}
                                        onChange={(e) => setUserForm({...userForm, displayName: e.target.value})}
                                    />
                                </FormControl>

                                <FormControl>
                                    <FormLabel>Role</FormLabel>
                                    <Select
                                        value={userForm.role}
                                        onChange={(e) => setUserForm({...userForm, role: e.target.value})}
                                    >
                                        <option value="user">Support Desk User</option>
                                        <option value="admin">Admin</option>
                                    </Select>
                                </FormControl>

                                <FormControl>
                                    <FormLabel>Status</FormLabel>
                                    <Select
                                        value={userForm.status}
                                        onChange={(e) => setUserForm({...userForm, status: e.target.value})}
                                    >
                                        <option value="approved">Approved</option>
                                        <option value="pending">Pending</option>
                                        <option value="rejected">Rejected</option>
                                        <option value="suspended">Suspended</option>
                                    </Select>
                                </FormControl>

                                {/* <FormControl>
                                    <FormLabel>Client Group</FormLabel>
                                    <Select
                                        value={userForm.clientGroup}
                                        onChange={(e) => setUserForm({...userForm, clientGroup: e.target.value})}
                                    >
                                        <option value="all">All Clients</option>
                                        {availableClients.map((client) => (
                                            <option key={client.value || client.id} value={client.value || client.id}>
                                                {client.label || client.name}
                                            </option>
                                        ))}
                                    </Select>
                                </FormControl> */}
                            </VStack>
                        </ModalBody>
                        <ModalFooter>
                            <Button variant="ghost" mr={3} onClick={onEditClose}>
                                Cancel
                            </Button>
                            <Button colorScheme="brand" onClick={handleEditUser}>
                                Update User
                            </Button>
                        </ModalFooter>
                    </ModalContent>
                </Modal>

                {/* Delete User Modal */}
                <Modal isOpen={isDeleteOpen} onClose={onDeleteClose}>
                    <ModalOverlay />
                    <ModalContent>
                        <ModalHeader>Confirm User Deletion</ModalHeader>
                        <ModalCloseButton />
                        <ModalBody>
                            <Alert status="warning" borderRadius="md">
                                <AlertIcon />
                                <Box>
                                    <Text fontWeight="bold">
                                        Are you sure you want to delete this user?
                                    </Text>
                                    <Text fontSize="sm" mt={2}>
                                        User: {currentUser?.displayName || currentUser?.name} ({currentUser?.email})
                                    </Text>
                                    <Text fontSize="sm" mt={2} color="red.500">
                                        This action cannot be undone. All user data will be permanently deleted.
                                    </Text>
                                </Box>
                            </Alert>
                        </ModalBody>
                        <ModalFooter>
                            <Button variant="ghost" mr={3} onClick={onDeleteClose}>
                                Cancel
                            </Button>
                            <Button colorScheme="red" onClick={handleDeleteUser}>
                                Delete User
                            </Button>
                        </ModalFooter>
                    </ModalContent>
                </Modal>

                {/* Bulk Operations Modal */}
                <Modal isOpen={isBulkOpen} onClose={onBulkClose}>
                    <ModalOverlay />
                    <ModalContent>
                        <ModalHeader>Bulk User Operations</ModalHeader>
                        <ModalCloseButton />
                        <ModalBody>
                            <VStack spacing={4} align="stretch">
                                <Text>
                                    Selected {selectedUsers.length} user(s) for bulk operation:
                                </Text>
                                
                                <SimpleGrid columns={2} spacing={3}>
                                    <Button
                                        leftIcon={<MdCheckCircle />}
                                        colorScheme="green"
                                        onClick={() => handleBulkOperation('approve')}
                                    >
                                        Approve All
                                    </Button>
                                    <Button
                                        leftIcon={<MdCancel />}
                                        colorScheme="red"
                                        onClick={() => handleBulkOperation('reject')}
                                    >
                                        Reject All
                                    </Button>
                                    <Button
                                        leftIcon={<MdBlock />}
                                        colorScheme="orange"
                                        onClick={() => handleBulkOperation('suspend')}
                                    >
                                        Suspend All
                                    </Button>
                                    <Button
                                        leftIcon={<MdDelete />}
                                        colorScheme="red"
                                        variant="outline"
                                        onClick={() => handleBulkOperation('delete')}
                                    >
                                        Delete All
                                    </Button>
                                </SimpleGrid>
                            </VStack>
                        </ModalBody>
                        <ModalFooter>
                            <Button variant="ghost" onClick={onBulkClose}>
                                Cancel
                            </Button>
                        </ModalFooter>
                    </ModalContent>
                </Modal>

                {/* View Audit Modal */}
                <Modal isOpen={isViewAuditOpen} onClose={closeAuditModal} size="xl">
                    <ModalOverlay />
                    <ModalContent>
                        <ModalHeader>
                            User Audit Logs: {currentAuditUser?.displayName || currentAuditUser?.name}
                        </ModalHeader>
                        <ModalCloseButton />
                        <ModalBody>
                            <Text fontSize="sm" color={textColor} mb={4}>
                                Showing recent login/logout activities for this user
                            </Text>
                            
                            <TableContainer>
                                <Table variant="simple" size="sm">
                                    <Thead>
                                        <Tr>
                                            <Th>Timestamp</Th>
                                            <Th>Action</Th>
                                            <Th>IP Address</Th>
                                            <Th>Session</Th>
                                        </Tr>
                                    </Thead>
                                    <Tbody>
                                        {auditLogs
                                            .filter(log => log.userEmail === currentAuditUser?.email)
                                            .slice(0, 10)
                                            .map((log, index) => (
                                                <Tr key={log.id || index}>
                                                    <Td>
                                                        <Text fontSize="sm">
                                                            {formatDateTime(log.createdAtUtc || log.timestamp)}
                                                        </Text>
                                                    </Td>
                                                    <Td>
                                                        <Badge
                                                            colorScheme={log.action === 'LOGIN' ? 'green' : 'blue'}
                                                            variant="subtle"
                                                        >
                                                            {log.action}
                                                        </Badge>
                                                    </Td>
                                                    <Td>
                                                        <Text fontSize="sm" fontFamily="mono">
                                                            {log.ipAddress}
                                                        </Text>
                                                    </Td>
                                                    <Td>
                                                        <Text fontSize="xs" color={textColor}>
                                                            {log.sessionId || 'N/A'}
                                                        </Text>
                                                    </Td>
                                                </Tr>
                                            ))}
                                    </Tbody>
                                </Table>
                            </TableContainer>
                        </ModalBody>
                        <ModalFooter>
                            <Button onClick={closeAuditModal}>
                                Close
                            </Button>
                        </ModalFooter>
                    </ModalContent>
                </Modal>
            </VStack>
        </Box>
    );
}

export default UserManagement;